import React from 'react';
import { motion } from 'framer-motion';
import { Check, Shield, Zap } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

export function Subscription() {
  const { t } = useLanguage();
  const features = t('subscription.features');

  return (
    <section id="team" className="py-24 md:py-32 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950" />
      
      {/* Accent glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-3xl" />

      <div className="max-w-4xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
        >
          <div 
            className="relative bg-gradient-to-br from-slate-900 to-slate-800 border-2 border-primary rounded-3xl p-8 md:p-12 overflow-hidden shadow-2xl shadow-primary/20"
            data-testid="subscription-card"
          >
            {/* Background effects */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary/20 blur-3xl" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-purple-500/20 blur-3xl" />
            
            {/* Content */}
            <div className="relative text-center">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-red-500/20 to-orange-500/20 border border-red-500/40 text-red-400 mb-6">
                <Shield size={18} />
                <span className="font-bold text-sm">{t('subscription.badge')}</span>
              </div>

              <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight mb-3">
                {t('subscription.title')}
              </h2>
              <p className="text-xl text-primary font-semibold mb-4">
                {t('subscription.subtitle')}
              </p>
              <p className="text-muted-foreground text-lg mb-10 max-w-2xl mx-auto">
                {t('subscription.description')}
              </p>

              {/* Price */}
              <div className="mb-10">
                <div className="flex items-end justify-center gap-2">
                  <span className="font-heading text-6xl md:text-8xl font-black text-white">{t('subscription.price')}</span>
                  <span className="text-2xl text-muted-foreground mb-3">{t('subscription.period')}</span>
                </div>
                <div className="flex items-center justify-center gap-4 mt-3">
                  <span className="text-muted-foreground">{t('subscription.commitment')}</span>
                  <span className="text-slate-600">•</span>
                  <span className="text-green-400 font-medium">{t('subscription.normalPrice')}</span>
                </div>
              </div>

              {/* Features grid */}
              <div className="grid md:grid-cols-2 gap-4 text-left max-w-2xl mx-auto">
                {features.map((feature, index) => (
                  <motion.div 
                    key={index} 
                    className="flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors"
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                  >
                    <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center shrink-0">
                      <Check className="w-4 h-4 text-primary" />
                    </div>
                    <span className="text-slate-200">{feature}</span>
                  </motion.div>
                ))}
              </div>

              {/* Bottom CTA hint */}
              <div className="mt-10 flex items-center justify-center gap-2 text-slate-400">
                <Zap size={18} className="text-yellow-500" />
                <span>Included with every €500 website package</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
